import type { Track } from './bridge';

type Identity = { platform: 'tx' | 'wy'; id: string };
type ShareableTrack = Pick<Track, 'id' | 'sourceType' | 'sourceId' | 'externalId' | 'metadata'> & { title?: string; artists?: string[] };
const failure = (code: string, message: string) => Object.assign(new Error(message), { code });

export function sharedRouteIdentity(platform: unknown, id: unknown): Identity | null {
  if (typeof id !== 'string') return null;
  if (platform === 'tx' && /^[A-Za-z0-9]{1,64}$/.test(id)) return { platform, id };
  if (platform === 'wy' && /^[1-9]\d{0,15}$/.test(id) && Number.isSafeInteger(Number(id))) return { platform, id };
  return null;
}

export function sharedTrackIdentity(track: ShareableTrack | null | undefined): Identity | null {
  if (!track || track.sourceType !== 'online' || track.sourceId === 'netease-cloud' || track.metadata?.neteaseCloud?.accountOnly === true) return null;
  const match = /^online:(?:yuxi|yuningxi):(tx|wy):([A-Za-z0-9]+)$/.exec(track.id);
  if (!match || track.externalId !== match[2] || track.metadata?.platform && track.metadata.platform !== match[1]) return null;
  return sharedRouteIdentity(match[1], match[2]);
}

export function sharedTrackPath(track: ShareableTrack | null | undefined): string | null {
  const identity = sharedTrackIdentity(track);
  return identity ? `/share/track/${identity.platform}/${identity.id}` : null;
}

export async function shareTrack(track: ShareableTrack, browser = navigator, origin = location.origin): Promise<'shared' | 'copied' | 'cancelled'> {
  const path = sharedTrackPath(track);
  if (!path) throw failure('TRACK_SHARE_UNAVAILABLE', '本地文件和私人音乐不能生成公开播放链接');
  const url = new URL(path, origin).href;
  const data = { title: track.title || '分享歌曲', text: [track.title, track.artists?.join(' / ')].filter(Boolean).join(' · '), url };
  if (typeof browser.share === 'function' && (!browser.canShare || browser.canShare(data))) {
    try { await browser.share(data); return 'shared'; }
    catch (error) { if ((error as Error)?.name === 'AbortError') return 'cancelled'; }
  }
  if (!browser.clipboard?.writeText) throw failure('SHARE_COPY_UNAVAILABLE', '浏览器未提供复制权限，请检查权限后重试');
  await browser.clipboard.writeText(url);
  return 'copied';
}

export async function readSharedTrack(identity: Identity, request: (path: string, init?: RequestInit) => Promise<any>, signal: AbortSignal): Promise<Track> {
  const params = new URLSearchParams({ kind: 'track', platform: identity.platform, id: identity.id });
  const data = await request(`/api/catalog?${params}`, { signal });
  const track = data?.track;
  const actual = sharedTrackIdentity(track);
  if (!actual || actual.platform !== identity.platform || actual.id !== identity.id) throw failure('CATALOG_TRACK_NOT_FOUND', '没有找到这首歌曲，链接可能已失效或歌曲已下架');
  return track;
}

type ShareStatus = 'loading' | 'ready' | 'gesture' | 'error';
export type SharedTrackState = { status: ShareStatus; track: Track | null; error: string };
type ShareRuntime = {
  access: { ready: boolean; authenticated: boolean };
  state: { currentId: string | null; isPlaying: boolean };
  ui: { lyricsOpen: boolean };
  api: (path: string, init?: RequestInit) => Promise<any>;
  normalizeTrack: (raw: Track) => Track;
  registerTracks: (tracks: Track[]) => void;
  pausePlaybackForNavigation?: () => void;
  reservePlaybackRequest: () => number;
  isPlaybackRequestCurrent: (ticket: number) => boolean;
  playbackSnapshot: () => { requestId: number };
  play: (id: string, list?: readonly string[]) => Promise<{ status: string; error?: unknown } | undefined>;
  notify: (text: string, type?: 'info' | 'success' | 'error') => void;
};

// The route owns metadata and its pending play only. A newer playback action
// elsewhere always wins; leaving the route does not stop a completed song.
export function createSharedTrackSession(runtime: ShareRuntime, changed: (state: SharedTrackState) => void) {
  let revision = 0, controller: AbortController | null = null, playbackTicket: number | null = null, pendingPlay = false;
  const cancel = () => {
    revision++; controller?.abort(); controller = null;
    if (pendingPlay && playbackTicket !== null && runtime.isPlaybackRequestCurrent(playbackTicket)) runtime.reservePlaybackRequest();
    pendingPlay = false; playbackTicket = null;
  };
  return {
    dispose: cancel,
    async load(platform: unknown, id: unknown) {
      cancel();
      if (runtime.access.ready && runtime.access.authenticated) {
        runtime.ui.lyricsOpen = false;
        // Stop the previous tab-local Audio before the catalog request. A
        // share navigation can take several seconds before playback resolves;
        // leaving the old source running makes its progress look like the new
        // share link and reintroduces the 0↔old-position jump during loading.
        runtime.pausePlaybackForNavigation?.();
      }
      const identity = sharedRouteIdentity(platform, id);
      if (!identity) { changed({ status: 'error', track: null, error: '歌曲链接无效，请检查平台和歌曲 ID' }); return; }
      if (!runtime.access.ready || !runtime.access.authenticated) return;
      const ticket = revision, abort = new AbortController(); controller = abort;
      playbackTicket = runtime.reservePlaybackRequest();
      const current = () => ticket === revision && !abort.signal.aborted && runtime.access.ready && runtime.access.authenticated && playbackTicket !== null && runtime.isPlaybackRequestCurrent(playbackTicket);
      const timeout = setTimeout(() => abort.abort(), 20_000);
      changed({ status: 'loading', track: null, error: '' });
      let track: Track | null = null;
      try {
        track = runtime.normalizeTrack(await readSharedTrack(identity, runtime.api, abort.signal));
        if (!current()) return;
        clearTimeout(timeout);
        runtime.registerTracks([track]);
        pendingPlay = true;
        const playing = runtime.play(track.id, [track.id]);
        playbackTicket = runtime.playbackSnapshot().requestId;
        const result = await playing;
        if (!current() || result?.status === 'stale') return;
        pendingPlay = false;
        if (runtime.state.currentId === track.id) runtime.ui.lyricsOpen = true;
        if (result?.status === 'error' && (result.error as Error)?.name === 'NotAllowedError') {
          changed({ status: 'gesture', track, error: '点击播放继续收听' });
          runtime.notify('浏览器暂停了自动播放，请点击播放继续收听', 'info');
        } else if (result?.status !== 'played') {
          changed({ status: 'error', track, error: '暂时无法播放这首歌曲，请点击重试' });
        } else changed({ status: 'ready', track, error: '' });
      } catch (error) {
        if (ticket !== revision || !runtime.access.authenticated || playbackTicket === null || !runtime.isPlaybackRequestCurrent(playbackTicket)) return;
        const code = (error as { code?: string })?.code;
        changed({ status: 'error', track, error: abort.signal.aborted ? '歌曲读取超时，请重试' : code === 'CATALOG_TRACK_NOT_FOUND' ? '没有找到这首歌曲，链接可能已失效或歌曲已下架' : '歌曲读取失败，请稍后重试' });
      } finally {
        clearTimeout(timeout);
        if (ticket === revision) pendingPlay = false;
      }
    },
  };
}
